/**
 * Phase 4 — inspection request/response schemas.
 *
 * Every endpoint that accepts user input parses through one of these
 * (via the `validate({ body | query | params })` middleware). The
 * `inspectionSchema` parser also validates the JSONB schema stored on
 * `inspection_template_versions.schema` — used both by the API on
 * publish and by the worker before generating PDFs.
 */

import { z } from 'zod';

import {
  INSPECTION_APPLIES_TO,
  INSPECTION_FORM_LAYOUTS,
  INSPECTION_PDF_LAYOUTS,
  INSPECTION_PREFILL_SOURCES,
  INSPECTION_SCHEDULE_CADENCES,
  INSPECTION_STATUSES,
  INSPECTION_TEMPLATE_STATUSES,
} from './inspection-constants.js';
import { cursorPaginationQuery } from './pagination.js';
import { WORK_ORDER_CATEGORIES, WORK_ORDER_SEVERITIES } from './work-order-constants.js';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

const metadata = z
  .record(z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .optional()
  .default({});

function csvList<T extends z.ZodTypeAny>(item: T) {
  return z
    .union([z.array(item), z.string()])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      if (Array.isArray(v)) return v;
      const parts = v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return parts as unknown as z.infer<T>[];
    })
    .pipe(z.array(item).optional());
}

// ---------- the JSONB form schema ----------

const idField = z.string().uuid();
const labelField = z.string().trim().min(1).max(200);
const helpTextField = z.string().trim().max(1_000).optional();

const selectOption = z.object({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(200),
  isDefect: z.boolean().optional(),
});

const questionKeyField = z.string().trim().min(1).max(80).optional();

const yesNoQuestion = z.object({
  id: idField,
  kind: z.literal('yes_no'),
  label: labelField,
  required: z.boolean().optional(),
  defectIf: z.enum(['yes', 'no']).optional(),
  allowNa: z.boolean().optional(),
  questionKey: questionKeyField,
  helpText: helpTextField,
});

const passFailQuestion = z.object({
  id: idField,
  kind: z.literal('pass_fail'),
  label: labelField,
  required: z.boolean().optional(),
  questionKey: questionKeyField,
  helpText: helpTextField,
});

const ratingQuestion = z
  .object({
    id: idField,
    kind: z.literal('rating'),
    label: labelField,
    scale: z.union([z.literal(3), z.literal(5)]),
    required: z.boolean().optional(),
    defectAtOrBelow: z.number().int().min(1).max(5).optional(),
    questionKey: questionKeyField,
    helpText: helpTextField,
  })
  .superRefine((q, ctx) => {
    if (q.defectAtOrBelow !== undefined && q.defectAtOrBelow > q.scale) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defectAtOrBelow'],
        message: 'Cannot exceed the rating scale',
      });
    }
  });

const shortTextQuestion = z.object({
  id: idField,
  kind: z.literal('short_text'),
  label: labelField,
  required: z.boolean().optional(),
  prefillSource: z.enum(INSPECTION_PREFILL_SOURCES).optional(),
  questionKey: questionKeyField,
  helpText: helpTextField,
});

const longTextQuestion = z.object({
  id: idField,
  kind: z.literal('long_text'),
  label: labelField,
  required: z.boolean().optional(),
  questionKey: questionKeyField,
  helpText: helpTextField,
});

const numberQuestion = z
  .object({
    id: idField,
    kind: z.literal('number'),
    label: labelField,
    unit: z.string().trim().max(40).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    required: z.boolean().optional(),
    prefillSource: z.enum(INSPECTION_PREFILL_SOURCES).optional(),
    questionKey: questionKeyField,
    helpText: helpTextField,
  })
  .superRefine((q, ctx) => {
    if (q.min !== undefined && q.max !== undefined && q.min > q.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['min'],
        message: 'min must be ≤ max',
      });
    }
  });

const singleSelectQuestion = z
  .object({
    id: idField,
    kind: z.literal('single_select'),
    label: labelField,
    options: z.array(selectOption).max(50),
    required: z.boolean().optional(),
    optionSource: z.enum(['org_users']).optional(),
    questionKey: questionKeyField,
    helpText: helpTextField,
  })
  .superRefine((q, ctx) => {
    if (q.optionSource === 'org_users') return;
    if (q.options.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Static single_select questions need at least 2 options',
      });
    }
  });

const multiSelectQuestion = z.object({
  id: idField,
  kind: z.literal('multi_select'),
  label: labelField,
  options: z.array(selectOption).min(2).max(50),
  required: z.boolean().optional(),
  questionKey: questionKeyField,
  helpText: helpTextField,
});

const photoQuestion = z
  .object({
    id: idField,
    kind: z.literal('photo'),
    label: labelField,
    minPhotos: z.number().int().min(0).max(20).optional(),
    maxPhotos: z.number().int().min(0).max(20).optional(),
    required: z.boolean().optional(),
    questionKey: questionKeyField,
    helpText: helpTextField,
  })
  .superRefine((q, ctx) => {
    if (q.minPhotos !== undefined && q.maxPhotos !== undefined && q.minPhotos > q.maxPhotos) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minPhotos'],
        message: 'minPhotos must be ≤ maxPhotos',
      });
    }
  });

const signatureQuestion = z.object({
  id: idField,
  kind: z.literal('signature'),
  label: labelField,
  required: z.boolean().optional(),
  collectPrintedName: z.boolean().optional(),
  questionKey: questionKeyField,
  helpText: helpTextField,
});

const sectionHeaderQuestion = z.object({
  id: idField,
  kind: z.literal('section_header'),
  label: labelField,
});

// `z.union` (not `discriminatedUnion`) is required here because rating /
// number / photo questions use `superRefine`, which produces ZodEffects,
// which `discriminatedUnion` rejects. The `kind` literal still narrows
// effectively at runtime; the union is small enough that the perf cost
// is negligible.
export const inspectionQuestion = z.union([
  yesNoQuestion,
  passFailQuestion,
  ratingQuestion,
  shortTextQuestion,
  longTextQuestion,
  numberQuestion,
  singleSelectQuestion,
  multiSelectQuestion,
  photoQuestion,
  signatureQuestion,
  sectionHeaderQuestion,
]);

export const inspectionSection = z.object({
  id: idField,
  title: z.string().trim().min(1).max(200),
  description: optionalText(2_000).optional(),
  questions: z.array(inspectionQuestion).min(0).max(200),
});

export const inspectionSchemaInput = z
  .object({
    sections: z.array(inspectionSection).min(0).max(50),
    layout: z.enum(INSPECTION_FORM_LAYOUTS).optional(),
    pdfLayout: z.enum(INSPECTION_PDF_LAYOUTS).optional(),
    pdfLayouts: z.array(z.enum(INSPECTION_PDF_LAYOUTS)).min(1).max(3).optional(),
  })
  .superRefine((s, ctx) => {
    // unique question IDs across the whole schema (the runner relies on
    // this to round-trip answers)
    const seen = new Set<string>();
    for (let i = 0; i < s.sections.length; i++) {
      const sec = s.sections[i]!;
      for (let j = 0; j < sec.questions.length; j++) {
        const q = sec.questions[j]!;
        if (seen.has(q.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sections', i, 'questions', j, 'id'],
            message: 'Duplicate question id within schema',
          });
        }
        seen.add(q.id);
      }
    }
  });
export type InspectionSchemaInput = z.infer<typeof inspectionSchemaInput>;

/**
 * Stricter variant: enforces the publish-readiness rules from the spec.
 * The builder may save a draft that fails these (e.g. empty sections);
 * publishing must pass.
 */
export const publishableInspectionSchema = inspectionSchemaInput.superRefine((s, ctx) => {
  if (s.sections.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sections'],
      message: 'Must have at least one section to publish',
    });
    return;
  }
  let totalQuestions = 0;
  for (let i = 0; i < s.sections.length; i++) {
    const sec = s.sections[i]!;
    if (sec.questions.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sections', i, 'questions'],
        message: 'Section must have at least one question to publish',
      });
    }
    totalQuestions += sec.questions.length;
  }
  if (totalQuestions === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sections'],
      message: 'At least one question is required to publish',
    });
  }
});

// ---------- templates ----------

export const createInspectionTemplateInput = z.object({
  name: z.string().trim().min(1).max(200),
  description: optionalText(2_000),
  appliesTo: z.enum(INSPECTION_APPLIES_TO),
  /** Optional initial schema. If omitted, the template starts with an empty draft schema. */
  schema: inspectionSchemaInput.optional(),
  /** Optional preset slug to clone from. Mutually exclusive with `schema`. */
  fromPreset: z.string().trim().min(1).max(80).optional(),
  metadata,
});
export type CreateInspectionTemplateInput = z.infer<typeof createInspectionTemplateInput>;

export const updateInspectionTemplateInput = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    description: optionalText(2_000).optional(),
    appliesTo: z.enum(INSPECTION_APPLIES_TO).optional(),
    metadata: metadata.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateInspectionTemplateInput = z.infer<typeof updateInspectionTemplateInput>;

export const updateInspectionVersionInput = z.object({
  schema: inspectionSchemaInput,
});
export type UpdateInspectionVersionInput = z.infer<typeof updateInspectionVersionInput>;

export const inspectionTemplateFiltersQuery = cursorPaginationQuery.extend({
  status: csvList(z.enum(INSPECTION_TEMPLATE_STATUSES)),
  appliesTo: csvList(z.enum(INSPECTION_APPLIES_TO)),
  includeArchived: z.coerce.boolean().optional().default(false),
  q: z.string().trim().max(120).optional(),
  sort: z.enum(['name', '-name', '-updated_at', '-created_at']).optional().default('name'),
});
export type InspectionTemplateFiltersQuery = z.infer<typeof inspectionTemplateFiltersQuery>;

// ---------- inspections ----------

export const createInspectionInput = z.object({
  templateId: z.string().uuid(),
  buildingId: z.string().uuid(),
  unitId: z
    .string()
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  inspectorUserId: z
    .string()
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  scheduledFor: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .transform((v) => v ?? null),
  notes: optionalText(8_000),
  metadata,
});
export type CreateInspectionInput = z.infer<typeof createInspectionInput>;

export const bulkCreateInspectionsInput = z
  .object({
    templateId: z.string().uuid(),
    buildingId: z.string().uuid(),
    inspectorUserId: z
      .string()
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    scheduledFor: z
      .string()
      .datetime({ offset: true })
      .nullish()
      .transform((v) => v ?? null),
    /** Either supply explicit unit IDs or set `allUnits: true` to pick every active unit. */
    unitIds: z.array(z.string().uuid()).max(2_000).optional(),
    allUnits: z.boolean().optional().default(false),
    notes: optionalText(8_000),
    metadata,
  })
  .superRefine((v, ctx) => {
    const hasUnits = Array.isArray(v.unitIds) && v.unitIds.length > 0;
    if (!hasUnits && !v.allUnits) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitIds'],
        message: 'Provide unitIds or set allUnits=true',
      });
    }
    if (hasUnits && v.allUnits) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['allUnits'],
        message: 'Cannot combine unitIds with allUnits',
      });
    }
  });
export type BulkCreateInspectionsInput = z.infer<typeof bulkCreateInspectionsInput>;

export const inspectionFiltersQuery = cursorPaginationQuery.extend({
  status: csvList(z.enum(INSPECTION_STATUSES)),
  buildingId: z.string().uuid().optional(),
  unitId: z.string().uuid().optional(),
  inspectorUserId: z.string().uuid().optional(),
  templateId: z.string().uuid().optional(),
  scheduledAfter: z.string().datetime({ offset: true }).optional(),
  scheduledBefore: z.string().datetime({ offset: true }).optional(),
  submittedAfter: z.string().datetime({ offset: true }).optional(),
  submittedBefore: z.string().datetime({ offset: true }).optional(),
  q: z.string().trim().max(120).optional(),
  sort: z
    .enum([
      'scheduled_for',
      '-scheduled_for',
      'submitted_at',
      '-submitted_at',
      '-updated_at',
      '-created_at',
    ])
    .optional()
    .default('-created_at'),
});
export type InspectionFiltersQuery = z.infer<typeof inspectionFiltersQuery>;

// ---------- answers ----------

const yesNoValue = z.object({
  kind: z.literal('yes_no'),
  value: z.enum(['yes', 'no', 'na']).nullable(),
});
const passFailValue = z.object({
  kind: z.literal('pass_fail'),
  value: z.enum(['pass', 'fail']).nullable(),
});
const ratingValue = z.object({
  kind: z.literal('rating'),
  value: z.number().int().min(1).max(5).nullable(),
});
const shortTextValue = z.object({
  kind: z.literal('short_text'),
  value: z.string().max(500).nullable(),
});
const longTextValue = z.object({
  kind: z.literal('long_text'),
  value: z.string().max(8_000).nullable(),
});
const numberValue = z.object({
  kind: z.literal('number'),
  value: z.number().nullable(),
});
const singleSelectValue = z.object({
  kind: z.literal('single_select'),
  value: z.string().max(80).nullable(),
});
const multiSelectValue = z.object({
  kind: z.literal('multi_select'),
  value: z.array(z.string().max(80)).max(50),
});
const photoValue = z.object({ kind: z.literal('photo'), value: z.null() });
const signatureValue = z.object({
  kind: z.literal('signature'),
  value: z
    .object({
      attachmentId: z.string().uuid(),
      printedName: z.string().max(200).nullish().transform((v) => v ?? null),
    })
    .nullable(),
});

export const inspectionAnswerValueInput = z.discriminatedUnion('kind', [
  yesNoValue,
  passFailValue,
  ratingValue,
  shortTextValue,
  longTextValue,
  numberValue,
  singleSelectValue,
  multiSelectValue,
  photoValue,
  signatureValue,
]);
export type InspectionAnswerValueInput = z.infer<typeof inspectionAnswerValueInput>;

export const upsertInspectionAnswerInput = z.object({
  value: inspectionAnswerValueInput,
  notes: optionalText(8_000),
  photoAttachmentIds: z.array(z.string().uuid()).max(20).optional().default([]),
});
export type UpsertInspectionAnswerInput = z.infer<typeof upsertInspectionAnswerInput>;

export const submitInspectionInput = z
  .object({
    notes: optionalText(8_000),
  })
  .optional()
  .default({});
export type SubmitInspectionInput = z.infer<typeof submitInspectionInput>;

// ---------- defect → WO ----------

export const createWorkOrdersFromDefectsInput = z.object({
  defects: z
    .array(
      z.object({
        questionId: z.string().uuid(),
        severity: z.enum(WORK_ORDER_SEVERITIES).optional().default('medium'),
        category: z.enum(WORK_ORDER_CATEGORIES).optional(),
        additionalNotes: optionalText(2_000),
        assignedTeamId: z
          .string()
          .uuid()
          .nullish()
          .transform((v) => v ?? null),
        assignedUserId: z
          .string()
          .uuid()
          .nullish()
          .transform((v) => v ?? null),
        dueAt: z
          .string()
          .datetime({ offset: true })
          .nullish()
          .transform((v) => v ?? null),
      }),
    )
    .min(1)
    .max(200),
});
export type CreateWorkOrdersFromDefectsInput = z.infer<typeof createWorkOrdersFromDefectsInput>;

// ---------- attachments (presign) ----------

export const INSPECTION_ATTACHMENT_KINDS = [
  'inspection_answer_photo',
  'inspection_signature',
] as const;
export type InspectionAttachmentKind = (typeof INSPECTION_ATTACHMENT_KINDS)[number];

export const inspectionAttachmentPresignInput = z.object({
  kind: z.enum(INSPECTION_ATTACHMENT_KINDS),
  questionId: z.string().uuid(),
  filename: z.string().trim().min(1).max(255),
  contentType: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'Invalid content type'),
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
});
export type InspectionAttachmentPresignInput = z.infer<typeof inspectionAttachmentPresignInput>;

// ---------- schedules ----------

const scheduleScopeInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('whole_building'), buildingId: z.string().uuid() }),
  z.object({ kind: z.literal('all_units_in_building'), buildingId: z.string().uuid() }),
  z.object({
    kind: z.literal('specific_units'),
    buildingId: z.string().uuid(),
    unitIds: z.array(z.string().uuid()).min(1).max(2_000),
  }),
  z.object({ kind: z.literal('common_area'), buildingId: z.string().uuid() }),
]);

export const createInspectionScheduleInput = z
  .object({
    templateId: z.string().uuid(),
    scope: scheduleScopeInput,
    cadence: z.enum(INSPECTION_SCHEDULE_CADENCES),
    customIntervalDays: z.number().int().positive().max(3_650).nullish(),
    dayOfWeek: z.number().int().min(0).max(6).nullish(),
    dayOfMonth: z.number().int().min(1).max(28).nullish(),
    assignToUserId: z
      .string()
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    startsAt: z.string().datetime({ offset: true }).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.cadence === 'custom' && !v.customIntervalDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customIntervalDays'],
        message: 'Required when cadence is "custom"',
      });
    }
    if (v.cadence !== 'custom' && v.customIntervalDays != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customIntervalDays'],
        message: 'Only set when cadence is "custom"',
      });
    }
  });
export type CreateInspectionScheduleInput = z.infer<typeof createInspectionScheduleInput>;

export const updateInspectionScheduleInput = createInspectionScheduleInput.innerType().partial();
export type UpdateInspectionScheduleInput = z.infer<typeof updateInspectionScheduleInput>;

export const listInspectionSchedulesQuery = cursorPaginationQuery.extend({
  templateId: z.string().uuid().optional(),
  buildingId: z.string().uuid().optional(),
  isPaused: z.coerce.boolean().optional(),
  q: z.string().trim().max(120).optional(),
  sort: z
    .enum(['next_run_at', '-next_run_at', '-updated_at', '-created_at'])
    .optional()
    .default('next_run_at'),
});
export type ListInspectionSchedulesQuery = z.infer<typeof listInspectionSchedulesQuery>;
